import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, injectable, inject } from 'inversify'
import { debounce } from 'lodash'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'

class AuditsSearchController {
    audits: any[] = []
    count = 0
    departmentId?: number | null = null
    roomId?: number | null = null
    searchTerm = ''
    page = 1
    limit = 25
    isLoading = false
    error: any = null
    sortBy = 'id'
    sortOrder: 'asc' | 'desc' = 'desc'
    searchScope = 'department'

    constructor(
        private api: Api,
        private session: SessionService
    ) {
        makeAutoObservable(this)
    }

    init(id: number, searchScope?: 'department' | 'room') {
        if (searchScope) this.searchScope = searchScope
        if (this.searchScope === 'department') {
            this.departmentId = id
        } else if (this.searchScope === 'room') {
            this.roomId = id
        }
        this.resetState()
    }

    setSearchTerm(value: string) {
        this.searchTerm = value
        this.resetSearchMeta()
    }

    setPage(page: number) {
        this.page = page
    }

    setLimit(limit: number) {
        this.limit = limit
        this.resetSearchMeta()
    }

    setSorting(sortBy: string, sortOrder: 'asc' | 'desc') {
        this.sortBy = sortBy
        this.sortOrder = sortOrder
        this.resetSearchMeta()
    }

    private debouncedSearchInternal = debounce(async () => {
        try {
            await this.search()
        } catch (e) {
            runInAction(() => {
                this.error = e
            })
        }
        runInAction(() => {
            this.isLoading = false
        })
    }, 500)

    debouncedSearch = () => {
        this.isLoading = true
        return this.debouncedSearchInternal()
    }

    search = async () => {
        if (this.searchScope === 'department' && !this.departmentId) return
        if (this.searchScope === 'room' && !this.roomId) return

        let results

        runInAction(() => {
            this.isLoading = true
            this.error = null
        })

        try {
            if (this.searchScope === 'department' && this.departmentId) {
                results = await this.api.Routes.Department.getAudits(this.departmentId, this.filter)
            } else if (this.searchScope === 'room' && this.roomId) {
                results = await this.api.Routes.Room.getAudits(this.roomId, this.filter)
            }
            const count = await this.fetchAuditsCount()

            runInAction(() => {
                this.audits = results
                this.count = count
                this.isLoading = false
            })
        } catch (e) {
            runInAction(() => {
                this.error = e
                this.isLoading = false
            })
        }
    }

    async fetchAuditsCount() {
        let res
        if (this.searchScope === 'department') {
            if (!this.departmentId) return 0
            res = await this.api.Routes.Department.getAuditsCount(this.departmentId, this.filter.where)
        } else {
            //Room Audit Count endpoint
            if (!this.roomId) return 0
            res = await this.api.Routes.Room.getAuditsCount(this.roomId, this.filter.where)
        }

        return res?.count ?? 0
    }

    resetSearchMeta() {
        this.page = 1
        this.audits = []
        this.count = 0
    }

    resetState() {
        this.resetSearchMeta()
        this.searchTerm = ''
        this.sortBy = 'id'
        this.sortOrder = 'desc'
    }

    get filter() {
        const filter: any = {
            include: [{
                relation: 'createdBy',
                scope: {
                    fields: ['id', 'firstName', 'lastName']
                }
            }],
            limit: this.limit,
            skip: (this.page - 1) * this.limit,
            order: `${this.sortBy} ${this.sortOrder}`
        }

        if (this.searchTerm.trim() !== '') {
            filter.where = {
                description: { like: `%${this.searchTerm.trim()}%` }
            }
        }

        return filter
    }

    async checkAuditStatus(auditId: number) {
        try {
            let audit
            if (this.searchScope === 'department' && this.departmentId) {
                audit = await this.api.Routes.Department.getAuditsById(this.departmentId, auditId)
            } else if (this.searchScope === 'room' && this.roomId) {
                audit = await this.api.Routes.Room.getAuditsById(this.roomId, auditId)
            } else {
                return null
            }

            return audit.status
        } catch (e) {
            runInAction(() => {
                this.error = e
            })
            return null
        }
    }

    /**
     * Run room audit
     * @param data
     */
    async runRoomAudit(data: {
        dateRangeStart: Date
        dateRangeEnd: Date
        description: string
        timeZone: string
    }) {
        if (!this.roomId) {
            throw new Error('No room ID specified')
        }

        // eslint-disable-next-line no-useless-catch
        try {
            const auditBody = {
                dateRangeStart: data.dateRangeStart,
                dateRangeEnd: data.dateRangeEnd,
                description: data.description,
                timeZone: data.timeZone,
                password: '' // Room audits don't need password protection
            }

            const result = await this.api.Routes.Room.postAuditsRun(this.roomId, auditBody)
            return result
        } catch (error) {
            throw error
        }
    }

    roomAuditDownloadLink(auditId: number) {
        if (!this.roomId) {
            throw new Error('No room ID specified')
        }
        return `${this.api.baseURL}Rooms/${this.roomId}/audits/${auditId}/download`
    }

}

decorate(injectable(), AuditsSearchController)
decorate(inject(Api), AuditsSearchController, 0)
decorate(inject(SessionService), AuditsSearchController, 1)

export { AuditsSearchController }
